import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Location} from '@angular/common';
import {convertToTitleCase, dropQueryParams, getQueryParamsAsObject, hasOnlyNumbers} from '@shared/utils/utils';
import {NavigationEnd, Router, Params} from '@angular/router';
import {ReplaySubject, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {PreviousRouteService} from '@core/services/previous-route.service';


interface Crumb {
    label: string;
    url: string;
    params: object;
}

@Component({
    selector: 'app-breadcrumbs',
    templateUrl: './breadcrumbs.component.html',
    styleUrls: ['./breadcrumbs.component.css'],
    standalone: false
})
export class BreadcrumbsComponent implements OnInit, OnDestroy {
    @Input() paramLabels$: ReplaySubject<object[]>;
    paramLabels: object[] = [];
    breadCrumbs: Crumb[] = [];
    showPrevious: boolean = true;
    previousUrl = '';
    previousQueryParams: Params = {};
    currentUrl = '';
    ngUnsubscribe$: Subject<any> = new Subject();

    constructor(private location: Location,
                private router: Router,
                private previousRouteService: PreviousRouteService) {}

    ngOnInit() {
        this.prepForBreadcrumb();
        this.buildBreadcrumb();

        if (this.paramLabels$) {
            this.paramLabels$
                .pipe(
                    takeUntil(this.ngUnsubscribe$),
                )
                .subscribe((paramLabels: object[]) => {
                        this.paramLabels = [...paramLabels.reverse()];  // enable LIFO with pop()
                        this.buildBreadcrumb();
                    }, (error) => console.log('error on paramLabels$.subscribe: ', error));
        }

        this.router.events
            .pipe(
                takeUntil(this.ngUnsubscribe$),
            )
            .subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.prepForBreadcrumb();
                if (!(this.paramLabels$)) {
                    this.buildBreadcrumb();
                }
            }
        });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next(undefined);
        this.ngUnsubscribe$.unsubscribe();
    }

    prepForBreadcrumb() {
        this.currentUrl = dropQueryParams(this.location.path());
        this.previousUrl = dropQueryParams(this.previousRouteService.getPreviousUrl());
        this.previousQueryParams = {...this.previousRouteService.getPreviousQueryParams()};
        if(this.previousQueryParams.tableState) {
            this.previousQueryParams = {...{'tableState': this.previousQueryParams['tableState']}};
        }
    }

    buildBreadcrumb() {
        this.breadCrumbs.length = 0;
        const previousPathArr: string[] = this.previousUrl.split('/');
        const pathArr: string[] = this.currentUrl.split('/');
        const lastIndex: number = pathArr.length - 1;
        let breadcrumbUrl = '';
        let breadcrumbLabel = '';
        let paramLabel = '';

        pathArr.map((segment, index) => {
            if ((index === lastIndex)
                && hasOnlyNumbers(segment)
                && pathArr.length > 1) {
                paramLabel = this.getParamLabel(segment);
                this.breadCrumbs.push({label: paramLabel ? paramLabel : 'Details',
                    url: dropQueryParams(this.currentUrl),
                    params: getQueryParamsAsObject(this.currentUrl)});
            } else if (index !== 0) {
                breadcrumbUrl = breadcrumbUrl + '/' + segment;
                if (hasOnlyNumbers(segment)) {
                    breadcrumbLabel = this.getParamLabel(segment);
                } else {
                    breadcrumbLabel = convertToTitleCase(segment.replace(/-/g, ' '));
                }
                this.breadCrumbs.push({label: dropQueryParams(breadcrumbLabel),
                    url: dropQueryParams(breadcrumbUrl), params: getQueryParamsAsObject(breadcrumbUrl)});
            }
        });
    }
    getParamLabel(param: string): string {
        if ((this.paramLabels.length === 0)) {
            return '';
        }
        const labelReplacementObject = this.paramLabels.pop();

        if (labelReplacementObject[param]) {
            return labelReplacementObject[param];
        }
        return 'LabelMismatch';
    }

    returnToPrevious() {
        this.previousRouteService.setIgnore();
        this.location.back();
    }
}
